import { auth, db } from '../../utils/firebase';
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
// 1. Import the Storage methods and your storage instance
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../utils/firebase'; // Make sure 'storage' is exported in firebase.js

// 2. Import your cropping component
import ImageCropper from '../../components/ImageCropper';

const PersonalProfileView = ({ onSave }) => {
    const [profileData, setProfileData] = useState({
        name: '',
        age: '',
        gender: '',
        location: '',
        bio: '',
        instagram: '',
        email: '',
        image: 'img/pprofile.png',
        skills: {
            hiking: 'beginner',
            running: 'beginner'
        },
        preferences: {
            pace: 'moderate',
            communication: 'chatty',
            adventureLevel: 'moderate'
        }
    });
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showAvatarDropdown, setShowAvatarDropdown] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedCity, setSelectedCity] = useState('');

    // For file upload
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    // 3. New states for cropping
    const [showCropper, setShowCropper] = useState(false);
    const [croppingURL, setCroppingURL] = useState(null);

    const countryCityMap = {
        'Australia': [
    'Adelaide', 'Brisbane', 'Melbourne', 'Perth', 'Sydney'
        ],
    'Belgium': ['Antwerp', 'Bruges', 'Brussels', 'Ghent', 'Liège'],
    'Bolivia': ['Cochabamba', 'La Paz', 'Santa Cruz', 'Sucre', 'Tarija'],
    'Canada': ['Calgary', 'Halifax', 'Montreal', 'Ottawa', 'Toronto', 'Vancouver'],
    'Colombia': ['Barranquilla', 'Bogotá', 'Bucaramanga', 'Cali', 'Cartagena', 'Medellín'],
    'Denmark': ['Aalborg', 'Aarhus', 'Copenhagen', 'Esbjerg', 'Odense'],
    'Ecuador': ['Cuenca', 'Guayaquil', 'Loja', 'Manta', 'Quito'],
    'Finland': ['Espoo', 'Helsinki', 'Oulu', 'Tampere', 'Turku'],
    'France': ['Biarritz', 'Bordeaux', 'Lyon', 'Marseille', 'Nice', 'Paris', 'Toulouse'],
    'Germany': ['Berlin', 'Cologne', 'Dresden', 'Frankfurt', 'Hamburg', 'Munich'],
    'Greece': ['Athens', 'Heraklion', 'Patras', 'Rhodes', 'Thessaloniki'],
    'Ireland': ['Cork', 'Dublin', 'Galway', 'Limerick', 'Waterford'],
    'Italy': ['Florence', 'Milan', 'Naples', 'Rome', 'Turin', 'Venice'],
    'Mexico': ['Cancún', 'Guadalajara', 'Mexico City', 'Monterrey', 'Playa del Carmen', 'Tulum'],
    'Netherlands': ['Amsterdam', 'Eindhoven', 'Rotterdam', 'The Hague', 'Utrecht'],
    'Norway': ['Bergen', 'Oslo', 'Stavanger', 'Tromsø', 'Trondheim'],
    'Portugal': ['Aveiro', 'Braga', 'Coimbra', 'Faro', 'Lisbon', 'Porto'],
    'Spain': ['Barcelona', 'Bilbao', 'Madrid', 'Gran Canaria', 'Seville', 'Valencia'],
    'Sweden': ['Gothenburg', 'Malmö', 'Stockholm', 'Uppsala', 'Västerås'],
    'Switzerland': ['Basel', 'Bern', 'Geneva', 'Lausanne', 'Zurich'],
    'United States': ['Atlanta', 'Berkeley', 'Chicago', 'Houston', 'Los Angeles', 'New York', 'Phoenix']
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    useEffect(() => {
        const fetchUserData = async () => {
            const currentUser = auth.currentUser;
            if (currentUser) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userDoc.exists()) {
                        const data = userDoc.data() || {};
                        // Merge default profileData with Firestore data so that missing fields are retained.
                        const updatedData = {
                            ...profileData,
                            ...data,
                            skills: {
                                hiking: data.skills?.hiking || 'beginner',
                                running: data.skills?.running || 'beginner'
                            }
                        };
                        setProfileData(updatedData);
                        if (data.location) {
                            const [city, country] = data.location.split(', ');
                            setSelectedCity(city);
                            setSelectedCountry(country);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            }
            setLoading(false);
        };

        fetchUserData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
            console.error("No user is signed in");
            return;
        }

        // Removed the required fields check so that you can update just one field.
        // The previous check was:
        // if (!profileData.name || !profileData.age || !profileData.gender || !profileData.location || 
        //     !profileData.bio || !profileData.instagram) {
        //     alert("Please fill in all required fields");
        //     return;
        // }

        try {
            await updateProfile(currentUser, {
                displayName: profileData.name
            });

            const updatedProfileData = {
                ...profileData,
                skills: profileData.skills || {
                    hiking: 'beginner',
                    running: 'beginner'
                }
            };

            await updateDoc(doc(db, 'users', currentUser.uid), updatedProfileData);

            onSave?.(updatedProfileData);
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating profile:", error);
            alert("Failed to update profile. Please try again.");
        }
    };

    // Existing avatar selection logic
    const handleAvatarSelect = async (avatarNumber) => {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const avatarUrl = `avatars/${avatarNumber}.png`;

        try {
            setProfileData(prev => ({
                ...prev,
                image: avatarUrl
            }));

            await updateDoc(doc(db, 'users', currentUser.uid), {
                image: avatarUrl
            });
        } catch (error) {
            console.error("Error updating avatar:", error);
            alert("Failed to update avatar. Please try again.");
        }
    };

    const handleCountryChange = (e) => {
        const country = e.target.value;
        setSelectedCountry(country);
        setSelectedCity('');
        setProfileData(prev => ({
            ...prev,
            location: ''
        }));
    };

    const handleCityChange = (e) => {
        const city = e.target.value;
        setSelectedCity(city);
        setProfileData(prev => ({
            ...prev,
            location: city ? `${city}, ${selectedCountry}` : ''
        }));
    };

    // 3. When user picks a file, show the cropper
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;
        setFile(selectedFile);
        setCroppingURL(URL.createObjectURL(selectedFile));
        setShowCropper(true);
    };

    // 3a. If user cancels cropping
    const handleCropCancel = () => {
        setShowCropper(false);
        setCroppingURL(null);
        setFile(null);
    };

    // 3b. If user completes cropping
    const handleCropComplete = (croppedFile) => {
        setFile(croppedFile); // final cropped file
        setCroppingURL(null);
        setShowCropper(false);
    };

    // 4. Upload to Firebase Storage
    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) {
            alert("No file selected");
            return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
            console.error("No user is signed in");
            return;
        }

        try {
            setUploading(true);
            const storageRef = ref(storage, `profilePictures/${currentUser.uid}/${file.name}`);
            await uploadBytes(storageRef, file);

            const downloadURL = await getDownloadURL(storageRef);

            await updateDoc(doc(db, 'users', currentUser.uid), {
                image: downloadURL
            });

            setProfileData(prev => ({
                ...prev,
                image: downloadURL
            }));

            alert("Profile picture updated!");
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Failed to upload. Please try again.");
        } finally {
            setUploading(false);
            setFile(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold' }}>Personal Profile</h1>
                <button
                    onClick={() => setIsEditing(!isEditing)}
                    style={{ color: '#6B46C1' }}
                >
                    {isEditing ? 'Cancel' : 'Edit'}
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Profile Image Circle + Avatar Selection */}
                <div style={{ textAlign: 'center', marginBottom: '80px', position: 'relative' }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                            src={profileData.image}
                            alt="Profile"
                            onClick={() => isEditing && setShowAvatarDropdown(!showAvatarDropdown)}
                            style={{ 
                                width: '128px', 
                                height: '128px', 
                                borderRadius: '50%',
                                border: '1px solid black',
                                margin: '0 auto',
                                objectFit: 'cover',
                                cursor: isEditing ? 'pointer' : 'default'
                            }}
                        />
                        {isEditing && (
                            <div style={{
                                position: 'absolute',
                                bottom: '-30px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                whiteSpace: 'nowrap',
                                color: '#6B46C1',
                                fontSize: '14px',
                                fontStyle: 'italic'
                            }}>
                                Click the current avatar to change it
                            </div>
                        )}
                    </div>
                    
                    {isEditing && showAvatarDropdown && (
                        <div 
                            style={{ 
                                position: 'absolute',
                                top: '140px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: '10px',
                                backgroundColor: 'white',
                                padding: '10px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                zIndex: 10,
                                justifyContent: 'center',
                                width: 'max-content',
                                flexWrap: 'wrap',
                                minWidth: '300px',
                                margin: '0 auto'
                            }}
                        >
                            {[1, 2, 3, 4, 5, 6].map((num) => (
                                <img
                                    key={num}
                                    src={`/avatars/${num}.png`}
                                    alt={`Avatar ${num}`}
                                    onClick={() => {
                                        handleAvatarSelect(num);
                                        setShowAvatarDropdown(false);
                                    }}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        border: profileData.image === `/avatars/${num}.png` ? '2px solid #6B46C1' : '2px solid transparent'
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* File input + Upload button (only shown in edit mode) */}
                {isEditing && (
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <label htmlFor="customFile" style={{ marginRight: '10px' }}>
                            Upload Custom Image:
                        </label>
                        <input
                            id="customFile"
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            style={{ marginRight: '10px' }}
                        />
                        <button
                            onClick={handleUpload}
                            disabled={uploading || !file}
                            style={{
                                padding: '8px 12px',
                                background: 'linear-gradient(to right, #6B46C1, #D53F8C)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            {uploading ? "Uploading..." : "Upload Custom Image"}
                        </button>
                    </div>
                )}

                {/* Existing form fields below */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {['name', 'age', 'gender', 'email', 'location', 'bio', 'instagram'].map((key) => {
                        if (!profileData.hasOwnProperty(key)) return null;
                        
                        const value = profileData[key];
                        const isEmailField = key === 'email';
                        
                        return (
                            <div key={key}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>
                                    {key.charAt(0).toUpperCase() + key.slice(1)}
                                </label>
                                {key === 'location' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <select
                                            name="country"
                                            value={selectedCountry}
                                            onChange={handleCountryChange}
                                            disabled={!isEditing}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                backgroundColor: 'white'
                                            }}
                                        >
                                            <option value="">Select Country</option>
                                            {Object.keys(countryCityMap).map(country => (
                                                <option key={country} value={country}>
                                                    {country}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            name="city"
                                            value={selectedCity}
                                            onChange={handleCityChange}
                                            disabled={!isEditing || !selectedCountry}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                backgroundColor: 'white'
                                            }}
                                        >
                                            <option value="">Select City</option>
                                            {selectedCountry && countryCityMap[selectedCountry].map(city => (
                                                <option key={city} value={city}>
                                                    {city}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ) : key === 'bio' ? (
                                    <textarea
                                        name={key}
                                        value={value}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        rows="3"
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            backgroundColor: isEmailField ? '#f3f4f6' : 'white'
                                        }}
                                    />
                                ) : key === 'gender' ? (
                                    <select
                                        name={key}
                                        value={value}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            backgroundColor: isEmailField ? '#f3f4f6' : 'white'
                                        }}
                                    >
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                ) : (
                                    <input
                                        type={key === 'age' ? 'number' : 'text'}
                                        name={key}
                                        value={value}
                                        onChange={handleChange}
                                        disabled={!isEditing || isEmailField}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            backgroundColor: isEmailField ? '#f3f4f6' : 'white'
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Your Experience Level</h3>
                    
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hiking Experience
                        </label>
                        <select
                            value={profileData.skills?.hiking || 'beginner'}
                            onChange={(e) => setProfileData(prev => ({
                                ...prev,
                                skills: {
                                    ...prev.skills,
                                    hiking: e.target.value
                                }
                            }))}
                            className="w-full p-2 border rounded-lg"
                            disabled={!isEditing}
                        >
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Running Experience
                        </label>
                        <select
                            value={profileData.skills?.running || 'beginner'}
                            onChange={(e) => setProfileData(prev => ({
                                ...prev,
                                skills: {
                                    ...prev.skills,
                                    running: e.target.value
                                }
                            }))}
                            className="w-full p-2 border rounded-lg"
                            disabled={!isEditing}
                        >
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>
                    </div>
                </div>

                {isEditing && (
                    <button
                        type="submit"
                        style={{
                            width: '100%',
                            padding: '12px',
                            background: 'linear-gradient(to right, #6B46C1, #D53F8C)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '9999px',
                            marginTop: '20px',
                            fontWeight: '600'
                        }}
                    >
                        Save Changes
                    </button>
                )}
            </form>

            {/* 6. Show the Cropper modal if user selected a file */}
            {showCropper && croppingURL && (
                <ImageCropper
                    imageSrc={croppingURL}
                    onCancel={handleCropCancel}
                    onComplete={handleCropComplete}
                />
            )}
        </div>
    );
};

export default PersonalProfileView;
